import { useState } from "react";
import { mutate } from "swr";
import type { SWRResponse } from "swr";

import { useAuth } from "@/contexts/auth-context";
import { useApiSWR } from "./use-api-client";
import {
  getWorkspace as fetchWorkspaceByID,
  getWorkspaceContext as fetchWorkspaceContext,
  listCompanyWorkspaces as fetchCompanyWorkspaceList,
  patchWorkspace as patchWorkspaceByID,
} from "@/lib/api/sdk-facade.gen";
import type {
  Workspace as GeneratedWorkspace,
  UpdateWorkspaceRequest,
} from "@/lib/generated/types.gen";
import type { Workspace, WorkspaceContextResponse } from "@/lib/types/workspace";
import { getCompanyWorkspacesKey, getWorkspaceContextKey } from "@/lib/context-cache-keys";
import { assertWorkspaceContextResponse } from "@/lib/types/workspace";

function transformGeneratedWorkspace(w: GeneratedWorkspace): Workspace {
  const withTotals = w as GeneratedWorkspace & {
    total_datasets?: number | null;
    total_signatures?: number | null;
  };

  return {
    ...w,
    billing_status: w.billing_status ?? "",
    total_datasets: typeof withTotals.total_datasets === "number" ? withTotals.total_datasets : 0,
    total_signatures:
      typeof withTotals.total_signatures === "number" ? withTotals.total_signatures : 0,
  };
}

export function useWorkspaceApi(): {
  loading: boolean;
  getWorkspaces: (companyId: number) => Promise<Workspace[]>;
  getWorkspace: (workspaceId: number) => Promise<Workspace>;
  getWorkspaceContext: (workspaceId: number) => Promise<WorkspaceContextResponse>;
  updateWorkspace: (workspaceId: number, data: UpdateWorkspaceRequest) => Promise<Workspace>;
  getCompanyWorkspacesKey: (companyId: number) => string | null;
  getWorkspaceContextKey: (workspaceId: number) => string | null;
  useWorkspaces: (
    companyId: number | null,
    enabled?: boolean,
  ) => SWRResponse<Workspace[] | undefined, Error>;
  useWorkspaceContext: (
    workspaceId: number | null,
  ) => SWRResponse<WorkspaceContextResponse | undefined, Error>;
} {
  const [loading, setLoading] = useState(false);
  const { userId } = useAuth();

  // Get workspaces for the active company.
  const getWorkspaces = async (companyId: number): Promise<Workspace[]> => {
    setLoading(true);
    try {
      const response = await fetchCompanyWorkspaceList({
        path: { company_id: companyId },
      });
      const items = Array.isArray(response.items) ? response.items : [];
      return items.map(transformGeneratedWorkspace);
    } finally {
      setLoading(false);
    }
  };

  const getWorkspace = async (workspaceId: number): Promise<Workspace> => {
    setLoading(true);
    try {
      const response = await fetchWorkspaceByID({
        path: { workspace_id: workspaceId },
      });
      return transformGeneratedWorkspace(response);
    } finally {
      setLoading(false);
    }
  };

  // SWR hook for company-scoped workspaces.
  const useWorkspaces = (
    companyId: number | null,
    enabled: boolean = true,
  ): SWRResponse<Workspace[] | undefined, Error> =>
    useApiSWR<Workspace[] | undefined>(
      enabled === true && companyId != null && userId != null
        ? getCompanyWorkspacesKey(userId, companyId)
        : null,
      enabled === true && companyId != null && userId != null
        ? async () => {
            const response = await fetchCompanyWorkspaceList({
              path: { company_id: companyId },
            });
            const items = Array.isArray(response.items) ? response.items : [];
            return items.map(transformGeneratedWorkspace);
          }
        : undefined,
    );

  // Get workspace context (workspace + user role + permissions + features)
  const getWorkspaceContext = async (workspaceId: number): Promise<WorkspaceContextResponse> => {
    setLoading(true);
    try {
      const resp = await fetchWorkspaceContext({
        path: { workspace_id: workspaceId },
      });
      return assertWorkspaceContextResponse(resp);
    } finally {
      setLoading(false);
    }
  };

  // SWR hook for workspace context (recommended for components)
  const useWorkspaceContext = (
    workspaceId: number | null,
  ): SWRResponse<WorkspaceContextResponse | undefined, Error> =>
    useApiSWR<WorkspaceContextResponse | undefined>(
      workspaceId != null && userId != null ? getWorkspaceContextKey(userId, workspaceId) : null,
      workspaceId != null && userId != null
        ? async () => {
            const resp = await fetchWorkspaceContext({
              path: { workspace_id: workspaceId },
            });
            return assertWorkspaceContextResponse(resp);
          }
        : undefined,
    );

  // Update workspace (requires ADMIN+ role)
  const updateWorkspace = async (
    workspaceId: number,
    data: UpdateWorkspaceRequest,
  ): Promise<Workspace> => {
    setLoading(true);
    try {
      const updated = await patchWorkspaceByID({
        path: { workspace_id: workspaceId },
        body: data,
      });
      if (userId != null) {
        await Promise.all([
          mutate(getCompanyWorkspacesKey(userId, updated.company_id)),
          mutate(getWorkspaceContextKey(userId, workspaceId)),
        ]);
      }
      return transformGeneratedWorkspace(updated);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    // Imperative API methods
    getWorkspaces,
    getWorkspace,
    getWorkspaceContext,
    updateWorkspace,
    getCompanyWorkspacesKey: (companyId: number) =>
      userId != null ? getCompanyWorkspacesKey(userId, companyId) : null,
    getWorkspaceContextKey: (workspaceId: number) =>
      userId != null ? getWorkspaceContextKey(userId, workspaceId) : null,
    // SWR hooks for reactive components
    useWorkspaces,
    useWorkspaceContext,
  };
}
